import EventEmitter from "events";
import {
    type KeyBackupInfo,
    VerificationPhase,
    type VerificationRequest,
    VerificationRequestEvent,
    CryptoEvent,
} from "matrix-js-sdk/src/crypto-api";
import { logger } from "matrix-js-sdk/src/logger";
import { type Device, type SecretStorage } from "matrix-js-sdk/src/matrix";

import { MatrixClientPeg } from "../MatrixClientPeg";
import { AccessCancelledError, accessSecretStorage } from "../SecurityManager";
import { asyncSome } from "../utils/arrays";
import { initialiseDehydrationIfEnabled } from "../utils/device/dehydration";
import { ENABLE_RECOVERY_KEY_BACKUP_OFFLINE, SKIP_CROSS_SIGNING_BOOTSTRAP } from "../dth/constants";
import { DthRecoveryKeyManager } from "../dth/clientHttp/DthRecoveryKeyManager";
import { DthSessionStorage } from "../dth/clientHttp/DthSessionStorage";
import { DthLoginPasswordCache } from "../dth/clientHttp/DthLoginPasswordCache";

export enum Phase {
    Loading = 0,
    Intro = 1,
    Busy = 2,
    Done = 3, // final done stage, but still showing UX
    ConfirmSkip = 4,
    Finished = 5, // UX can be closed
}

/**
 * Logic for setting up 4S and/or verifying the user's device: a process requiring
 * ongoing interaction with the user, as distinct from InitialCryptoSetupStore which
 * a (usually) non-interactive process that happens immediately after registration.
 */
export class SetupEncryptionStore extends EventEmitter {
    private started?: boolean;
    public phase?: Phase;
    public verificationRequest: VerificationRequest | null = null;
    public backupInfo: KeyBackupInfo | null = null;
    // ID of the key that the secrets we want are encrypted with
    public keyId: string | null = null;
    // Descriptor of the key that the secrets we want are encrypted with
    public keyInfo: SecretStorage.SecretStorageKeyDescription | null = null;
    public hasDevicesToVerifyAgainst?: boolean;

    public static sharedInstance(): SetupEncryptionStore {
        if (!window.mxSetupEncryptionStore) window.mxSetupEncryptionStore = new SetupEncryptionStore();
        return window.mxSetupEncryptionStore;
    }

    public start(): void {
        if (this.started) {
            return;
        }
        this.started = true;
        this.phase = Phase.Loading;

        const cli = MatrixClientPeg.safeGet();
        cli.on(CryptoEvent.VerificationRequestReceived, this.onVerificationRequest);
        cli.on(CryptoEvent.UserTrustStatusChanged, this.onUserTrustStatusChanged);

        const requestsInProgress = cli.getCrypto()!.getVerificationRequestsToDeviceInProgress(cli.getUserId()!);
        if (requestsInProgress.length) {
            // If there are multiple, we take the most recent. Equally if the user sends another request from
            // another device after this screen has been shown, we'll switch to the new one, so this
            // generally doesn't support multiple requests.
            this.setActiveVerificationRequest(requestsInProgress[requestsInProgress.length - 1]);
        }

        this.fetchKeyInfo();
    }

    public stop(): void {
        if (!this.started) {
            return;
        }
        this.started = false;
        this.verificationRequest?.off(VerificationRequestEvent.Change, this.onVerificationRequestChange);

        const cli = MatrixClientPeg.get();
        if (cli) {
            cli.removeListener(CryptoEvent.VerificationRequestReceived, this.onVerificationRequest);
            cli.removeListener(CryptoEvent.UserTrustStatusChanged, this.onUserTrustStatusChanged);
        }
    }

    public async fetchKeyInfo(): Promise<void> {
        if (!this.started) return; // bail if we were stopped
        const cli = MatrixClientPeg.safeGet();
        // When SKIP_CROSS_SIGNING_BOOTSTRAP is true, cross-signing keys are never stored in 4S.
        // Check m.megolm_backup.v1 instead to determine whether a recovery key has been set up.
        const secretToCheck = SKIP_CROSS_SIGNING_BOOTSTRAP ? "m.megolm_backup.v1" : "m.cross_signing.master";
        const keys = await cli.secretStorage.isStored(secretToCheck);
        if (keys === null || Object.keys(keys).length === 0) {
            this.keyId = null;
            this.keyInfo = null;
        } else {
            // If the secret is stored under more than one key, we just pick an arbitrary one
            this.keyId = Object.keys(keys)[0];
            this.keyInfo = keys[this.keyId];
        }

        const ownUserId = cli.getUserId()!;
        const crypto = cli.getCrypto()!;
        // do we have any other verified devices which are E2EE which we can verify against?
        const userDevices: Iterable<Device> =
            (await crypto.getUserDeviceInfo([ownUserId])).get(ownUserId)?.values() ?? [];
        this.hasDevicesToVerifyAgainst = await asyncSome(userDevices, async (device) => {
            // Ignore dehydrated devices. MSC3814 proposes that devices
            // should set a `dehydrated` flag in the device key.
            if (device.dehydrated) return false;

            // ignore devices without an identity key
            if (!device.getIdentityKey()) return false;

            const verificationStatus = await crypto.getDeviceVerificationStatus(ownUserId, device.deviceId);
            return !!verificationStatus?.signedByOwner;
        });

        // Attempt silent auto-restore using the locally cached session password +
        // the encrypted recovery key stored in IndexedDB. If successful the phase
        // will transition to Done/Finished without any user interaction. On failure
        // (or when no 4S key is configured) we fall through to the normal Intro phase.
        if (!this.keyInfo || !(await this.tryAutoUsePassPhrase())) {
            this.setPhase(Phase.Intro);
            this.emit("update");
        }
    }

    /**
     * Transitions the phase, enforcing that once Phase.Done is reached only
     * Phase.Finished is a valid next state. Any other transition from Done is
     * silently ignored.
     */
    private setPhase(newPhase: Phase): void {
        if (this.phase === Phase.Done && newPhase !== Phase.Finished) {
            logger.debug(
                `SetupEncryptionStore.setPhase: blocking transition Done → ${Phase[newPhase]}`,
            );
            return;
        }
        this.phase = newPhase;
    }

    /**
     * Try to automatically complete the recovery flow by decrypting the locally
     * stored recovery key with the session password.
     *
     * @returns `true` if the attempt was made (regardless of outcome — phase
     *          transitions are handled by `usePassPhrase` itself), `false` if
     *          the preconditions are not met and the caller should fall back to
     *          the normal Intro phase.
     */
    private async tryAutoUsePassPhrase(): Promise<boolean> {
        // Guard: only run once per singleton lifetime. stop()/start() cycles caused by
        // React remounts (StrictMode double-invoke, parent re-renders) must not repeat
        // the auto-restore flow.
        try {
            const username = await DthSessionStorage.tryGetUsername();
            if (!ENABLE_RECOVERY_KEY_BACKUP_OFFLINE) return false;
            if (!username) return false;
            if (!(await DthRecoveryKeyManager.has(username))) return false;
            // Read from in-memory cache — SecurityManager.getSecretStorageKey will
            // clear the entry from the cache after the auto-restore attempt.
            const storedPassword = await DthLoginPasswordCache.get(username);
            if (!storedPassword) return false;
            // Mark before the async call so concurrent/re-entrant calls are blocked too.
            logger.debug("SetupEncryptionStore.tryAutoUsePassPhrase: preconditions met, attempting auto-restore");
            await this.usePassPhrase();
            return true;
        } catch (e) {
            logger.warn("SetupEncryptionStore.tryAutoUsePassPhrase: failed", e);
            return false;
        }
    }

    public async usePassPhrase(): Promise<void> {
        // Guard against re-entrant calls (e.g. auto-restore + manual click racing).
        if (this.phase === Phase.Busy) {
            logger.debug("SetupEncryptionStore.usePassphrase: already busy, ignoring duplicate call");
            return;
        }
        if (this.phase === Phase.Done) {
            logger.debug("SetupEncryptionStore.usePassphrase: already done, ignoring duplicate call");
            return;
        }

        logger.debug("SetupEncryptionStore.usePassphrase");
        this.setPhase(Phase.Busy);
        this.emit("update");
        try {
            const cli = MatrixClientPeg.safeGet();
            const backupInfo = (await cli.getCrypto()?.getKeyBackupInfo()) ?? null;
            this.backupInfo = backupInfo;
            this.emit("update");

            await new Promise((resolve: (value?: unknown) => void, reject: (reason?: any) => void) => {
                accessSecretStorage(async (): Promise<void> => {
                    // `accessSecretStorage` will call `boostrapCrossSigning` and `bootstrapSecretStorage`, so that
                    // should be enough to ensure that our device is correctly cross-signed.
                    //
                    // The remaining tasks (device dehydration and restoring key backup) may take some time due to
                    // processing many to-device messages in the case of device dehydration, or having many keys to
                    // restore in the case of key backups, so we allow the dialog to advance before this.
                    //
                    // However, we need to keep the 4S key cached, so we stay inside `accessSecretStorage`.
                    logger.debug(
                        "SetupEncryptionStore.usePassphrase: secret storage ready; dehydration and " +
                        "backup restore will continue in the background",
                    );

                    // When cross-signing bootstrap is skipped, `bootstrapSecretStorage` may complete
                    // without calling `getSecretStorageKey` (no cross-signing secrets to store).
                    // Explicitly load the backup private key here — before resolving — to guarantee
                    // the user is prompted for their recovery key and that
                    // `getSessionBackupPrivateKey()` returns non-null for the `isDone` check below.
                    if (SKIP_CROSS_SIGNING_BOOTSTRAP && this.keyInfo) {
                        await cli.getCrypto()?.loadSessionBackupPrivateKeyFromSecretStorage();
                    }

                    resolve();

                    await initialiseDehydrationIfEnabled(cli);

                    if (backupInfo) {
                        // Carica la chiave privata del backup da 4S nella sessione
                        await cli.getCrypto()?.loadSessionBackupPrivateKeyFromSecretStorage();

                        // Registra/attiva il backup in-memory per questa sessione.
                        // Necessario perché `getActiveSessionBackupVersion()` ritorni la versione attiva.
                        await cli.getCrypto()?.checkKeyBackupAndEnable();

                        // Ripristina le chiavi dal backup ora che la sessione è connessa al backup
                        await cli.getCrypto()?.restoreKeyBackup();
                    }
                }).catch(reject);
            });

            // When cross-signing is skipped, check if the backup private key was loaded from
            // secret storage — that confirms the user actually provided the recovery key.
            const isDone = SKIP_CROSS_SIGNING_BOOTSTRAP
                ? !!(await cli.getCrypto()?.getSessionBackupPrivateKey())
                : !!(await cli.getCrypto()?.getCrossSigningKeyId());
            if (isDone) {
                // During auto-restore, skip the "device verified" confirmation screen and go
                // straight to Finished — the spinner stays visible until the home page loads.
                this.setPhase(Phase.Done);
                logger.debug(`SetupEncryptionStore.usePassphrase: done`);
                this.emit("update");
            }
        } catch (e) {
            if (e instanceof AccessCancelledError) {
                logger.debug("SetupEncryptionStore.usePassphrase: user cancelled access to secret storage");
            } else {
                logger.error("SetupEncryptionStore.usePassphrase: error", e);
            }

            this.setPhase(Phase.Intro);
            this.emit("update");
        }
    }

    private onUserTrustStatusChanged = async (userId: string): Promise<void> => {
        if (userId !== MatrixClientPeg.safeGet().getSafeUserId()) return;
        // When cross-signing is skipped this event is irrelevant; ignore it.
        if (SKIP_CROSS_SIGNING_BOOTSTRAP) return;
        const publicKeysTrusted = await MatrixClientPeg.safeGet().getCrypto()?.getCrossSigningKeyId();
        if (publicKeysTrusted) {
            this.setPhase(Phase.Done);
            this.emit("update");
        }
    };

    public onVerificationRequest = (request: VerificationRequest): void => {
        this.setActiveVerificationRequest(request);
    };

    public onVerificationRequestChange = async (): Promise<void> => {
        if (this.verificationRequest?.phase === VerificationPhase.Cancelled) {
            this.verificationRequest.off(VerificationRequestEvent.Change, this.onVerificationRequestChange);
            this.verificationRequest = null;
            this.emit("update");
        } else if (this.verificationRequest?.phase === VerificationPhase.Done) {
            this.verificationRequest.off(VerificationRequestEvent.Change, this.onVerificationRequestChange);
            this.verificationRequest = null;
            // At this point, the verification has finished, we just need to wait for
            // cross signing to be ready to use, so wait for the user trust status to
            // change (or change to DONE if it's already ready).
            // When cross-signing is skipped, check if the backup private key is in memory
            // (loaded from 4S) rather than checking cross-signing. Fall back to trusted when
            // there is no backup configured (this.backupInfo null means no backup was found).
            const publicKeysTrusted = SKIP_CROSS_SIGNING_BOOTSTRAP
                ? !!(await MatrixClientPeg.safeGet().getCrypto()?.getSessionBackupPrivateKey()) || !this.backupInfo
                : !!(await MatrixClientPeg.safeGet().getCrypto()?.getCrossSigningKeyId());
            this.setPhase(publicKeysTrusted ? Phase.Done : Phase.Busy);
            this.emit("update");
        }
    };

    public skip(): void {
        this.setPhase(Phase.ConfirmSkip);
        this.emit("update");
    }

    public skipConfirm(): void {
        this.finished();
    }

    public returnAfterSkip(): void {
        this.setPhase(Phase.Intro);
        this.emit("update");
    }

    public done(): void {
        this.finished();
    }

    private finished() {
        this.setPhase(Phase.Finished);
        this.emit("update");

        // Clear from memory immediately — this is the only use for the recovery path.
        DthLoginPasswordCache.clear();
    }

    private async setActiveVerificationRequest(request: VerificationRequest): Promise<void> {
        if (!this.started) return; // bail if we were stopped
        if (request.otherUserId !== MatrixClientPeg.safeGet().getUserId()) return;

        if (this.verificationRequest) {
            this.verificationRequest.off(VerificationRequestEvent.Change, this.onVerificationRequestChange);
        }
        this.verificationRequest = request;
        await request.accept();
        request.on(VerificationRequestEvent.Change, this.onVerificationRequestChange);
        this.emit("update");
    }

    public lostKeys(): boolean {
        return !SKIP_CROSS_SIGNING_BOOTSTRAP && (!this.hasDevicesToVerifyAgainst && !this.keyInfo);
    }
}
